import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useBreakpointValue
} from "@chakra-ui/react";
import { isNil } from "lodash/fp";
import dynamic from "next/dynamic";
import { Suspense, memo, useEffect } from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import { PriceSelection } from "components/PriceSelection";
import useCartHooks from "components/hooks/Cart/useCartHooks";
import { RegionType } from "graphql/generated";
import { CURRENT_COUNTRY } from "src/constants/Countries";
import {
  OTC_PRODUCTS_HK,
  OTC_PRODUCTS as SG_OTC_PRODUCTS,
  STRIPE_PRODUCT_MAPPING
} from "src/constants/Products";
import {
  isAddingCartState,
  priceModalSelectedPriceState,
  priceModalSelectedProductState,
  priceModalShowPriceStep,
  selectedConsultTypeState
} from "src/states";
import { checkIsCountryHaveTextConsult } from "src/utils";
import getNameProp from "src/utils/getNameProp";

const ConsultTypes = dynamic(() =>
  import("components/ConsultTypes").then((components) => components.ConsultTypes)
);

interface Props {
  onConfirmHandler?: (values?) => void;
  isSuggestion?: boolean;
}

const OTC_PRODUCTS = CURRENT_COUNTRY === RegionType.Sg ? SG_OTC_PRODUCTS : OTC_PRODUCTS_HK;
const otcProductUniqueNames = OTC_PRODUCTS.map((product) => STRIPE_PRODUCT_MAPPING[product]).filter(
  Boolean
);

const PriceModal = ({ onConfirmHandler, isSuggestion }: Props) => {
  const selectedProduct = useRecoilValue(priceModalSelectedProductState);
  const [selectedPrice, setPreselectPrice] = useRecoilState(priceModalSelectedPriceState);
  const [selectedConsultType, setSelectedConsultType] = useRecoilState(selectedConsultTypeState);
  const isAdding = useRecoilValue(isAddingCartState);
  const [showPriceSelectionStep, setShowPriceSelectionStep] =
    useRecoilState(priceModalShowPriceStep);
  const size = useBreakpointValue({ base: "full", md: "5xl" });
  const { onConfirmPriceClick, onClosePriceModal, isModalOpened } = useCartHooks(isSuggestion);

  const onConfirm = (item) => {
    if (!isAdding) {
      if (onConfirmHandler) {
        onConfirmHandler(item);
      } else {
        onConfirmPriceClick(item, selectedConsultType);
      }
    }
    setPreselectPrice(null);
  };

  const onCloseModal = () => {
    if (!isAdding) {
      onClosePriceModal();
      // Reset the selected consult type
      setShowPriceSelectionStep(!checkIsCountryHaveTextConsult());
    }
    setPreselectPrice(null);
  };

  useEffect(() => {
    // If the selected product is OTC, show the price selection step (ignore the consultation type selection step)
    if (
      isModalOpened &&
      selectedProduct &&
      otcProductUniqueNames.includes(selectedProduct.uniqName)
    ) {
      setShowPriceSelectionStep(true);
    }
  }, [selectedProduct, setShowPriceSelectionStep, isModalOpened]);

  return (
    <Modal onClose={onCloseModal} size={size} isOpen={isModalOpened}>
      <Suspense fallback={<div />}>
        <ModalOverlay />
        <ModalContent borderRadius="0px" margin={{ base: "0px", md: "auto" }} bg="default.100">
          <ModalCloseButton color="primary.500" fontSize="xl" zIndex={1} top={4} right={4} />
          <ModalBody
            pt={{ base: "60px", md: "60px" }}
            pb={{ base: "60px", md: "30px" }}
            px={{ base: "30px", md: "60px" }}
          >
            <Flex justifyContent="center" {...getNameProp("price-modal-wrapper")}>
              {showPriceSelectionStep ? (
                <PriceSelection
                  preselect={selectedPrice?.priceId || ""}
                  productId={selectedProduct?.id}
                  autoProcess={false}
                  onConfirm={onConfirm}
                  isLoading={isAdding}
                  onlyShowOTC={
                    isNil(selectedProduct?.isSubscriptionPrice)
                      ? false
                      : !selectedProduct?.isSubscriptionPrice
                  }
                />
              ) : (
                <ConsultTypes
                  selectedType={selectedConsultType}
                  onSelectType={setSelectedConsultType}
                  onClickedNextBtn={() => setShowPriceSelectionStep(true)}
                  shouldShowMoreInit={false}
                />
              )}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Suspense>
    </Modal>
  );
};

export default memo(PriceModal);
